<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50 pb-20">
    <!-- Header section -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Bus Schedule</h1>
            <p class="text-blue-100">View the latest campus bus times and routes</p>
            <div class="mt-2 flex flex-col sm:flex-row items-center justify-center gap-2">
              <span class="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-900 font-semibold text-sm">
                <span class="material-symbols-outlined text-base mr-1">calendar_today</span>
                {{ currentDay }}
              </span>
              <span class="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-900 font-semibold text-sm">
                <span class="material-symbols-outlined text-base mr-1">schedule</span>
                {{ currentTime }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Loading state -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <div class="w-12 h-12 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin mb-4"></div>
        <p class="text-blue-800 font-medium">Loading bus schedules...</p>
      </div>

      <div v-else>
        <!-- Trip Search Component -->
        <BusTripSearch
          :routes="routes"
          :matching-routes-count="filteredRoutesByTrip.length"
          @search="handleTripSearch"
        />

        <!-- Filter and refresh controls -->
        <BusFilter
          :campuses="campuses"
          v-model:selected-campus="selectedCampus"
          :refreshing="refreshing"
          @refresh="fetchBusData"
        />

        <!-- Favorite routes section -->
        <div v-if="favoriteRoutes.length > 0" id="favorites" class="mb-10">
          <FavoriteRoutes
            :favorite-routes="favoriteRoutes"
            :routes="displayedRoutes"
            @toggleFavorite="toggleFavorite"
            :get-next-bus="getNextBus"
          />
        </div>

        <!-- All routes section -->
        <div id="routes" class="mb-8">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-bold text-blue-900 flex items-center">
              <span class="material-symbols-outlined mr-2 text-blue-600">directions_bus</span>
              {{ tripSearchActive ? 'Matching Routes' : 'Campus Shuttle Routes' }}
              <span v-if="displayedRoutes.length > 0" class="ml-2 text-base font-normal text-gray-600">
                ({{ displayedRoutes.length }})
              </span>
            </h2>
          </div>
          
          <div v-if="displayedRoutes.length > 0" class="grid grid-cols-1 gap-6">
            <RouteCard
              v-for="route in displayedRoutes"
              :key="route.id"
              :route="route"
              :favorite-routes="favoriteRoutes"
              :get-next-bus="getNextBus"
              :get-today-times="getTodayTimes"
              @toggleFavorite="toggleFavorite"
            />
          </div>

          <!-- Empty state -->
          <div v-else class="bg-white rounded-xl shadow-sm p-12 text-center">
            <span class="material-symbols-outlined text-6xl text-gray-300 mb-4">directions_bus_filled</span>
            <h3 class="text-lg font-medium text-gray-700 mb-2">
              {{ tripSearchActive ? 'No routes match your search' : 'No routes available' }}
            </h3>
            <p class="text-gray-500 mb-6">
              {{ tripSearchActive ? 'Try adjusting your search criteria' : 'No routes available for the selected campus' }}
            </p>
            <button
              v-if="tripSearchActive"
              @click="clearTripSearch"
              class="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-md"
            >
              <span class="material-symbols-outlined mr-2">clear</span>
              Clear Search
            </button>
          </div>

          <!-- Information note -->
          <div class="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-100">
            <div class="flex items-start">
              <span class="material-symbols-outlined text-blue-600 mr-3 mt-0.5">info</span>
              <div>
                <h4 class="font-semibold text-blue-900 mb-2">Important Information</h4>
                <p class="text-sm text-blue-800">
                  Please arrive at the bus stop <strong>5 minutes before</strong> the scheduled departure time. 
                  Times may vary due to traffic or operational reasons.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import BusFilter from '@/components/bus/BusFilter.vue';
import FavoriteRoutes from '@/components/bus/FavoriteRoutes.vue';
import BusTripSearch from '@/components/bus/BusTripSearch.vue';
import RouteCard from '@/components/bus/RouteCard.vue';
import busService from '@/services/busService';

export default {
  name: "BusSchedulePage",
  components: {
    AppHeader,
    BottomNav,
    BusFilter,
    FavoriteRoutes,
    BusTripSearch,
    RouteCard
  },
  data() {
    return {
      routes: [],
      campuses: [],
      selectedCampus: "",
      favoriteRoutes: [],
      loading: true,
      refreshing: false,
      error: null,
      currentDay: '',
      currentTime: '',
      timer: null,
      // Trip search state
      tripSearchOrigin: '',
      tripSearchDestination: ''
    };
  },
  computed: {
    tripSearchActive() {
      return this.tripSearchOrigin !== '' || this.tripSearchDestination !== '';
    },
    
    // Filter routes by trip search (origin/destination)
    filteredRoutesByTrip() {
      if (!this.tripSearchActive) {
        return this.routes;
      }
      
      return this.routes.filter(route => {
        const pickupLocations = route.pickup_locations || [];
        const dropoffLocations = route.dropoff_locations || [];
        
        let matchesOrigin = true;
        let matchesDestination = true;
        
        if (this.tripSearchOrigin) {
          matchesOrigin = pickupLocations.some(loc => 
            loc.toLowerCase().includes(this.tripSearchOrigin.toLowerCase())
          );
        }
        
        if (this.tripSearchDestination) {
          matchesDestination = dropoffLocations.some(loc => 
            loc.toLowerCase().includes(this.tripSearchDestination.toLowerCase())
          );
        }
        
        return matchesOrigin && matchesDestination;
      });
    },
    
    // Apply both trip search and campus filter
    displayedRoutes() {
      let routes = this.filteredRoutesByTrip;
      
      // Then apply campus filter if selected
      if (this.selectedCampus) {
        routes = routes.filter(route => {
          const pickupLocations = route.pickup_locations || [];
          const dropoffLocations = route.dropoff_locations || [];
          return pickupLocations.some(location =>
            location.toLowerCase().includes(this.selectedCampus.toLowerCase())
          ) || dropoffLocations.some(location =>
            location.toLowerCase().includes(this.selectedCampus.toLowerCase())
          );
        });
      }
      
      return routes;
    },
    
    filteredRoutes() {
      // Keep for backward compatibility, but use displayedRoutes
      return this.displayedRoutes;
    }
  },
  created() {
    this.loadFavorites();
    this.fetchBusData();
    this.updateCurrentTime();
    this.timer = setInterval(() => {
      this.updateCurrentTime();
    }, 1000);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    async fetchBusData() {
      this.loading = true;
      this.error = null;
      try {
        this.routes = await busService.getAllRoutes();
        this.campuses = busService.getUniqueCampuses(this.routes);
        this.loading = false;
      } catch (error) {
        console.error('Error fetching bus data:', error);
        this.error = 'Failed to load bus schedules. Please try again.';
        this.$store.dispatch('showAlert', {
          message: 'Failed to load bus schedules. Please try again.',
          type: 'error'
        });
        this.loading = false;
      }
    },
    refreshSchedule() {
      this.refreshing = true;
      this.fetchBusData().finally(() => {
        setTimeout(() => {
          this.refreshing = false;
        }, 500);
      });
    },
    toggleFavorite(routeId) {
      if (this.favoriteRoutes.includes(routeId)) {
        this.favoriteRoutes = this.favoriteRoutes.filter(id => id !== routeId);
      } else {
        this.favoriteRoutes.push(routeId);
      }
      localStorage.setItem('favoriteRoutes', JSON.stringify(this.favoriteRoutes));
    },
    loadFavorites() {
      try {
        const favorites = localStorage.getItem('favoriteRoutes');
        if (favorites) {
          this.favoriteRoutes = JSON.parse(favorites);
        }
      } catch (error) {
        console.error('Error loading favorites:', error);
        this.favoriteRoutes = [];
      }
    },
    isFavorite(routeId) {
      return this.favoriteRoutes.includes(routeId);
    },
    updateCurrentTime() {
      const now = new Date();
      this.currentDay = now.toLocaleString('en-US', { weekday: 'long' });
      this.currentTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },
    // --- UX: Next bus logic with color and time breakdown ---
    getNextBus(route) {
      const now = new Date();
      const weekday = now.toLocaleString('en-US', { weekday: 'long' });
      let allTimes = [];
      let dayInfo = '';
      let foundDay = false;

      if (route.schedule) {
        for (const direction in route.schedule) {
          const schedule = route.schedule[direction];
          if (schedule[weekday]) {
            allTimes = allTimes.concat(schedule[weekday]);
            dayInfo = weekday;
            foundDay = true;
          }
          if (!foundDay && schedule["Monday-Friday"] && now.getDay() >= 1 && now.getDay() <= 5) {
            allTimes = allTimes.concat(schedule["Monday-Friday"]);
            dayInfo = "Monday-Friday";
            foundDay = true;
          }
          if (!foundDay && schedule["Saturday"] && now.getDay() === 6) {
            allTimes = allTimes.concat(schedule["Saturday"]);
            dayInfo = "Saturday";
            foundDay = true;
          }
          if (!foundDay && schedule["Sunday"] && now.getDay() === 0) {
            allTimes = allTimes.concat(schedule["Sunday"]);
            dayInfo = "Sunday";
            foundDay = true;
          }
        }
      }
      allTimes = allTimes.filter(t => t && typeof t === 'string' && t.match(/^\d{2}:\d{2}$/));
      allTimes = allTimes.sort();

      let nextTime = null;
      let nextTimeStr = '';
      let countdownStr = '';
      let color = 'bg-green-600';
      if (allTimes.length > 0) {
        for (const t of allTimes) {
          const [h, m] = t.split(':').map(Number);
          const busDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
          if (busDate > now) {
            nextTime = busDate;
            nextTimeStr = t;
            break;
          }
        }
      }
      if (nextTime) {
        const diffMs = nextTime - now;
        const hours = Math.floor(diffMs / 3600000);
        const minutes = Math.floor((diffMs % 3600000) / 60000);
        const seconds = Math.floor((diffMs % 60000) / 1000);
        countdownStr =
          (hours > 0 ? `${hours}h ` : '') +
          (minutes > 0 ? `${minutes}m ` : '') +
          `${seconds}s`;
        if (diffMs <= 5 * 60000) color = 'bg-red-600';
        else if (diffMs <= 15 * 60000) color = 'bg-yellow-400 text-blue-900';
        else color = 'bg-green-600';
      }
      return {
        nextTime,
        timeStr: nextTimeStr,
        countdownStr: nextTime ? countdownStr : '',
        color,
        dayInfo: dayInfo || 'No schedule for today'
      };
    },
    getTodayTimes(route) {
      const now = new Date();
      const weekday = now.toLocaleString('en-US', { weekday: 'long' });
      let allTimes = [];
      let foundDay = false;
      if (route.schedule) {
        for (const direction in route.schedule) {
          const schedule = route.schedule[direction];
          if (schedule[weekday]) {
            allTimes = allTimes.concat(schedule[weekday]);
            foundDay = true;
          }
          if (!foundDay && schedule["Monday-Friday"] && now.getDay() >= 1 && now.getDay() <= 5) {
            allTimes = allTimes.concat(schedule["Monday-Friday"]);
            foundDay = true;
          }
          if (!foundDay && schedule["Saturday"] && now.getDay() === 6) {
            allTimes = allTimes.concat(schedule["Saturday"]);
            foundDay = true;
          }
          if (!foundDay && schedule["Sunday"] && now.getDay() === 0) {
            allTimes = allTimes.concat(schedule["Sunday"]);
            foundDay = true;
          }
        }
      }
      allTimes = allTimes.filter(t => t && typeof t === 'string' && t.match(/^\d{2}:\d{2}$/));
      return allTimes.sort();
    },
    handleTripSearch({ origin, destination }) {
      this.tripSearchOrigin = origin;
      this.tripSearchDestination = destination;
    },
    clearTripSearch() {
      this.tripSearchOrigin = '';
      this.tripSearchDestination = '';
    }
  }
};
</script>

<style scoped>
.material-symbols-outlined {
  vertical-align: middle;
}
.min-w-\[180px\] {
  min-width: 180px;
}
</style>
